import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import InsuranceConventionGeneralDataComponent from './InsuranceConventionGeneralDataComponent';
import { CURRENCIES_DICTIONARY_QUERY } from 'features/common/queries';
import {
  INSURANCE_CONVENTION_TYPES_QUERY,
  INSURANCE_TRACKING_TYPES_QUERY,
  INSURANCE_AMOUNT_TYPES_QUERY,
  LEASING_INSURED_AMOUNT_TYPES_QUERY,
  INSURANCE_TYPES_QUERY
} from '../../queries';
import { ADD_UPDATE_CONVENTION_GENERAL_DATA } from '../../mutations';
import { CONVENTION_GENERAL_DATA_UPDATED } from '../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { insuranceConventionValidator } from '../../validators';
import { get, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { conventionNameVar, conventionCurrencyCodeVar } from '../../variables';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';

const InsuranceConventionGeneralDataContainer = ({ data, productId, onRedirect }) => {
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens();
  const [saving, setSaving] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionValidator);
  const addToast = useToast();
  const { t } = useTranslation();

  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  const { data: currenciesDictionaryData } = useQuery(CURRENCIES_DICTIONARY_QUERY);

  const { data: conventionTypesData } = useQuery(INSURANCE_CONVENTION_TYPES_QUERY);

  const { data: trackingTypesData } = useQuery(INSURANCE_TRACKING_TYPES_QUERY);

  const { data: amountTypesData } = useQuery(INSURANCE_AMOUNT_TYPES_QUERY);

  const { data: leasingAmountTypesData } = useQuery(LEASING_INSURED_AMOUNT_TYPES_QUERY);

  const { data: insuranceTypesData } = useQuery(INSURANCE_TYPES_QUERY);

  useEffect(() => {
    resetLens(data);
  }, [data, resetLens]);

  useEffect(() => {
    const code = get(lens.productCode) ? get(lens.productCode) : '-';
    const name = get(lens.productName) ? get(lens.productName) : '-';
    const concatenatedNameVar = `${name} / ${code}`;
    conventionNameVar(concatenatedNameVar);
  }, [lens]);

  useEffect(() => {
    const currencyCode = get(lens.currencyCode) ? get(lens.currencyCode) : '-';
    conventionCurrencyCodeVar(currencyCode);
  }, [lens.currencyCode]);

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const [addOrUpdateInsuranceConvention] = useMutation(ADD_UPDATE_CONVENTION_GENERAL_DATA, {
    onError: () => {
      setSaving(false);
    }
  });

  const handleSave = useCallback(() => {
    const insuranceConvention = get(lens);
    if (!validate(insuranceConvention)) return;
    setSaving(true);
    // eslint-disable-next-line no-unused-vars
    const { genericFilterTemplateId, ...rest } = insuranceConvention;
    addOrUpdateInsuranceConvention({ variables: { input: rest } });
  }, [lens, addOrUpdateInsuranceConvention, validate]);

  useSubscription(CONVENTION_GENERAL_DATA_UPDATED, {
    onData: ({
      data: {
        data: { insuranceConventionGeneralDataUpdated }
      },
      client
    }) => {
      if (insuranceConventionGeneralDataUpdated?.convention?.calculationInfoChanged) return;
      if (!productId) onRedirect(insuranceConventionGeneralDataUpdated.convention.productId);
      resetLens(insuranceConventionGeneralDataUpdated.convention);
      setSaving(false);
      client.cache.modify({
        fields: {
          insuranceConventionsList(existing) {
            return [existing, insuranceConventionGeneralDataUpdated.convention];
          }
        }
      });
      addToast(t('InsuranceConventions.ConventionUpdated'), 'success');
    }
  });

  return (
    <InsuranceConventionGeneralDataComponent
      lens={lens}
      currencies={currenciesDictionaryData?.currenciesDictionary}
      conventionTypes={conventionTypesData?.insuranceConventionTypes}
      trackingTypes={trackingTypesData?.insuranceTrackingTypes}
      amountTypes={amountTypesData?.insuranceAmountTypes}
      leasingInsuredAmountTypes={leasingAmountTypesData?.leasingInsuredAmountTypes}
      insuranceTypes={insuranceTypesData?.insuranceTypes}
      periodicities={Array.from({ length: 12 }, (_, i) => (i + 1).toString())}
      isDirty={isDirty}
      saving={saving}
      onSave={handleSave}
      validation={validation}
    />
  );
};

InsuranceConventionGeneralDataContainer.propTypes = {
  data: PropTypes.object,
  productId: PropTypes.number,
  onRedirect: PropTypes.func
};

export default InsuranceConventionGeneralDataContainer;
